import React from "react";
import { Card, Form, Button, Spinner } from "react-bootstrap";
import {
  StickyResponsePanel,
  HideButton,
  ApiCardTitle,
  ApiCardPath,
  MethodBadge,
  EndpointPath,
  ResponseContainer,
  ParamInput,
} from "../styles/StyledComponents";

/**
 * Renders the response panel for the selected endpoint
 * @param {Object} props - Component props
 * @param {Object} props.selectedEndpoint - The selected endpoint
 * @param {Object} props.endpointParams - The parameters for the endpoint
 * @param {Function} props.handleParamChange - Function to call when parameters change
 * @param {Function} props.handleSubmit - Function to call when form is submitted
 * @param {boolean} props.loading - Whether the API call is in progress
 * @param {Object} props.response - The API response
 * @param {string} props.baseUrl - The base URL for the API
 * @param {Function} props.toggleResponsePanel - Function to toggle the response panel
 * @returns {JSX.Element}
 */
const ResponsePanel = ({
  selectedEndpoint,
  endpointParams,
  handleParamChange,
  handleSubmit,
  loading,
  response,
  baseUrl,
  toggleResponsePanel,
}) => {
  if (!selectedEndpoint) {
    return null;
  }

  return (
    <StickyResponsePanel>
      <HideButton variant="light" size="sm" onClick={toggleResponsePanel} title="Hide response panel">
        &times;
      </HideButton>
      <Card.Header>
        <ApiCardTitle>
          <MethodBadge method={selectedEndpoint.method}>{selectedEndpoint.method}</MethodBadge>
          <span>{selectedEndpoint.name}</span>
        </ApiCardTitle>
        <ApiCardPath>
          <EndpointPath>{selectedEndpoint.path}</EndpointPath>
        </ApiCardPath>
        <div className="mt-2 text-muted small">
          Using: <code>{baseUrl}</code>
        </div>
      </Card.Header>
      <Card.Body>
        <h5>Parameters</h5>
        <Form>
          {Object.keys(endpointParams).map(param => (
            <Form.Group key={param} className="mb-3">
              <Form.Label>{param}</Form.Label>
              {typeof endpointParams[param] === "object" ? (
                <ParamInput
                  as="textarea"
                  rows={5}
                  value={JSON.stringify(endpointParams[param], null, 2)}
                  onChange={e => {
                    try {
                      const parsed = JSON.parse(e.target.value);
                      handleParamChange(param, parsed);
                    } catch (err) {
                      // If not valid JSON yet, store as string
                      handleParamChange(param, e.target.value);
                    }
                  }}
                />
              ) : (
                <ParamInput
                  type="text"
                  value={endpointParams[param] || ""}
                  onChange={e => handleParamChange(param, e.target.value)}
                />
              )}
            </Form.Group>
          ))}
          <Button variant="primary" onClick={handleSubmit} disabled={loading}>
            {loading ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Loading...
              </>
            ) : (
              "Send Request"
            )}
          </Button>
        </Form>

        {response && (
          <>
            <h5 className="mt-4">Response</h5>
            <ResponseContainer>{JSON.stringify(response, null, 2)}</ResponseContainer>
          </>
        )}
      </Card.Body>
    </StickyResponsePanel>
  );
};

export default ResponsePanel;
